import {
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import {
  pmsOccupancyRefusal,
  useOccupancySource,
} from "../hooks/useOccupancySource";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MessageIcon from "@mui/icons-material/ChatBubbleOutline";
import SearchIcon from "@mui/icons-material/Search";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoomOutlined";
import NoMeetingRoomIcon from "@mui/icons-material/NoMeetingRoomOutlined";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScannerOutlined";
import PowerIcon from "@mui/icons-material/PowerSettingsNew";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isScreenOnline } from "@shared/types/foyer";
import type { ScreenRole, SignagePlaybackReport } from "@shared/types/foyer";
import {
  foyerApi,
  type BulkActionResult,
  type ScreenRecord,
} from "../http/foyer";
import DeviceDetailDialog from "./DeviceDetailDialog";
import BulkActionDialog, { type BulkAction } from "./BulkActionDialog";
import ProvisioningDialog from "./ProvisioningDialog";
import PowerScheduleDialog from "./PowerScheduleDialog";
import { PermissionGate } from "../auth/access";
import { openScreenPreview } from "../utils/preview";
import {
  platformLabel,
  relativeTime,
  roleLabel,
  signagePlaybackLabel,
} from "../utils/devicePresentation";
import { PageHeader } from "./PageHeader";
import type { Translate } from "../i18n/translate";
import { errorText } from "../utils/apiError";

/** Role badge for the Devices table.
 *
 *  The two non-TV roles get filled chips so a screen that is NOT an in-room TV
 *  is obvious at a glance in a list of forty: a door sign is the first
 *  PHI-bearing surface, and a signage screen plays to a public room. An in-room
 *  TV - the overwhelming majority - stays a quiet outlined chip. */
function RoleChip({
  role,
  id,
  playback,
  online,
  lastSeenAt,
}: {
  role: ScreenRole | undefined;
  id: string;
  /** The panel's own last word on what it is doing. Signage screens only. */
  playback?: SignagePlaybackReport;
  /** Whether the DEVICE itself has heartbeated recently - see the staleness
   *  note below. Same `isScreenOnline` the Status column already renders. */
  online: boolean;
  lastSeenAt?: string;
}) {
  const { t } = useTranslation("premises");
  const isSign = role === "door-sign";
  const isSignage = role === "signage";
  // A signage screen wears what it is DOING, not what it is. "Online" was true
  // of every screen through four separate outages this week - playing, idle,
  // frozen and serving nothing but 404s all looked identical from this table.
  const rawState = isSignage ? signagePlaybackLabel(t, playback) : null;
  // The report is the panel's LAST word, not its current one. Once the device
  // has gone silent, "Playing" is exactly the same kind of dated fact
  // `DeviceDetailDialog` already dates everywhere else (see
  // device-console-honesty in project memory) - it just never reached this
  // chip. A green "Signage · Playing" on an offline row is how hasselbacken/302
  // sat unnoticed for three days: the Status column said "offline" one cell
  // over and nothing joined the two. Only the "ok" reading gets visibly
  // downgraded (to amber, with the age spelled out) - a stale "warn"/"error"
  // stays exactly as alarming as it already was, offline or not.
  const stale = rawState != null && !online;
  const state =
    stale && rawState.severity === "ok"
      ? { ...rawState, severity: "warn" as const }
      : rawState;
  const label = state
    ? stale
      ? t("devicesView.roleChip.signageStateOffline", { state: state.text })
      : t("devicesView.roleChip.signageState", { state: state.text })
    : roleLabel(t, role);
  const color = state
    ? state.severity === "error"
      ? "error"
      : state.severity === "warn"
        ? "warning"
        : "success"
    : isSign
      ? "secondary"
      : isSignage
        ? "primary"
        : "default";
  return (
    <Tooltip
      title={
        state
          ? stale
            ? state.detail
              ? t("devicesView.roleChip.staleWithDetail", {
                  ago: relativeTime(t, lastSeenAt),
                  detail: state.detail,
                })
              : t("devicesView.roleChip.stale", {
                  ago: relativeTime(t, lastSeenAt),
                })
            : state.detail || t("devicesView.roleChip.playingLoop")
          : isSign
            ? t("devicesView.roleChip.doorSignHint")
            : isSignage
              ? t("devicesView.roleChip.signageHint")
              : t("devicesView.roleChip.tvHint")
      }
    >
      <Chip
        size="small"
        label={label}
        color={color}
        variant={isSign || isSignage ? "filled" : "outlined"}
        data-testid={`device-role-${id}`}
      />
    </Tooltip>
  );
}

interface Props {
  premiseId: string;
  /** Whether the door-sign role is offered. Door signs are a care/PHI surface,
   *  so the role controls only appear on care premises (computed by the parent
   *  via `isCarePremise`); hotels never see them. */
  allowDoorSign?: boolean;
  /** Whether the signage role is offered - i.e. the place bought Signage. */
  allowSignage?: boolean;
  /** Rooms for this premise. `occupied` drives the per-row check-in
   *  status - written by the manual toggle, the PMS scheduler, or a
   *  future PMS vendor integration. */
  rooms: { id: string; name?: string; occupied?: boolean }[];
  /**
   * Care premises: occupancy is DERIVED from the admission record, so this view
   * shows it and never writes it. The server already refuses the write
   * (`occupancy_managed_by_admission`, 409) because a manual flip would desync
   * the roster from the TV - and checking a room "out" would fire the checkout
   * wipe on an admitted resident. Offering a control that can only fail is worse
   * than not offering it: the operator's real action is a discharge under
   * Residents. Hotels keep the toggle, which is their actual front-desk workflow.
   */
  occupancyDerived?: boolean;
  /** Whether screen sharing exists on this premise (false on care - the boxes
   *  ship with Chromecast removed). Passed straight to the detail dialog, which
   *  owns every casting row. */
  showGuestCasting?: boolean;
}

/** Wi-Fi RSSI (dBm) → a human-readable quality band + chip colour, so weak
 *  devices jump out of the roster at a glance. Bands match the detail dialog
 *  (`formatSignal`). Returns `null` when there's no Wi-Fi reading (Ethernet, or
 *  the TV never reported one) - the caller renders a muted placeholder. */
function signalBand(dBm?: number): {
  label: "excellent" | "good" | "fair" | "weak";
  color: "success" | "warning" | "error";
  dBm: number;
} | null {
  if (typeof dBm !== "number" || !Number.isFinite(dBm)) return null;
  if (dBm >= -55) return { label: "excellent", color: "success", dBm };
  if (dBm >= -67) return { label: "good", color: "success", dBm };
  if (dBm >= -75) return { label: "fair", color: "warning", dBm };
  return { label: "weak", color: "error", dBm };
}

/** Signal cell: a colour-coded quality chip (green/amber/red) with the exact
 *  dBm in a tooltip. Wired or reading-less devices show a muted dash. */
function SignalCell({ dBm, ethIp }: { dBm?: number; ethIp?: string }) {
  const { t } = useTranslation("premises");
  const band = signalBand(dBm);
  if (!band) {
    return (
      <Tooltip
        title={
          ethIp
            ? t("devicesView.signal.ethernetHint")
            : t("devicesView.signal.noReading")
        }
      >
        <Typography variant="body2" color="text.secondary">
          {ethIp ? t("devicesView.signal.wired") : "-"}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={t("devicesView.signal.dBm", { value: band.dBm })}>
      <Chip
        size="small"
        variant={band.color === "success" ? "outlined" : "filled"}
        color={band.color}
        label={t(`devicesView.signal.${band.label}`)}
      />
    </Tooltip>
  );
}

function formatUptime(t: Translate, ms?: number): string {
  if (!ms || ms < 0) return "-";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days) return t("premises:devicesView.uptime.daysHours", { days, hours });
  if (hours)
    return t("premises:devicesView.uptime.hoursMinutes", { hours, mins });
  return t("premises:devicePresentation.duration.minutes", { n: mins });
}

type SortKey =
  | "room"
  | "role"
  | "checkin"
  | "model"
  | "platform"
  | "status"
  | "signal"
  | "uptime"
  | "lastSeen"
  | "app"
  | "streaming"
  | "firmware";

export default function DevicesView({
  premiseId,
  allowDoorSign = false,
  allowSignage = false,
  rooms,
  occupancyDerived = false,
  showGuestCasting = true,
}: Props) {
  // A PMS that owns check-ins: the chip asks it, or is read-only.
  const { driver } = useOccupancySource(premiseId);
  const here = useMemo(() => ({ premiseId }), [premiseId]);
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const [screens, setScreens] = useState<ScreenRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // Role filter: "all" (default), or only TVs / only door signs.
  const [roleFilter, setRoleFilter] = useState<"all" | ScreenRole>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<ScreenRecord | null>(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [powerOpen, setPowerOpen] = useState(false);
  // The pending bulk action (reboot / message) whose dialog is open, plus the
  // frozen screen set it targets (so it survives the selection being cleared).
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const [bulkTargets, setBulkTargets] = useState<string[]>([]);
  // Room ids with an in-flight occupancy write, to disable the chip.
  const [occupancyBusy, setOccupancyBusy] = useState<Set<string>>(new Set());
  // Live occupancy from the server, refreshed alongside screens. Seeded
  // from the editor draft so the column renders before the first poll,
  // but the server is authoritative - a check-in/out elsewhere shows up
  // within one refresh instead of waiting for a page reload.
  const [liveRooms, setLiveRooms] = useState<
    { id: string; occupied?: boolean }[] | null
  >(null);

  const refresh = useCallback(async () => {
    try {
      const [next, roomList] = await Promise.all([
        foyerApi.listScreens(premiseId),
        foyerApi.listRooms(premiseId).catch(() => null),
      ]);
      setScreens(next);
      if (roomList) setLiveRooms(roomList);
      setError(null);
      // Drop any selected ids that are no longer present.
      setSelected((prev) => {
        const live = new Set(next.map((s) => s.id));
        const filtered = new Set<string>();
        prev.forEach((id) => live.has(id) && filtered.add(id));
        return filtered;
      });
    } catch (err) {
      setError(errorText(err, t));
    }
  }, [premiseId, t]);

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 5_000);
    return () => window.clearInterval(t);
  }, [refresh]);

  // Room id → guest occupancy, for the per-row check-in status. Prefer
  // the live server list; fall back to the editor draft until it loads.
  const occupancyByRoom = useMemo(
    () => new Map((liveRooms ?? rooms).map((r) => [r.id, !!r.occupied])),
    [liveRooms, rooms]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return screens.filter((s) => {
      // Role gate first - "all" passes everything; otherwise match the
      // screen's role (absent => "tv" for back-compat).
      if (roleFilter !== "all" && (s.role ?? "tv") !== roleFilter) return false;
      if (!q) return true;
      const room = (s.roomId ?? "").toLowerCase();
      const model = (s.model ?? "").toLowerCase();
      const id = s.id.toLowerCase();
      const platform = platformLabel(s.live?.platform).toLowerCase();
      const streaming = (s.live?.streamingAppVersion ?? "").toLowerCase();
      const firmware = (s.live?.firmware ?? "").toLowerCase();
      const role = roleLabel(t, s.role).toLowerCase();
      return (
        room.includes(q) ||
        model.includes(q) ||
        id.includes(q) ||
        platform.includes(q) ||
        streaming.includes(q) ||
        firmware.includes(q) ||
        role.includes(q)
      );
    });
  }, [screens, query, roleFilter, t]);

  // Column sorting. `sortKey === null` preserves the server/insertion order
  // (the original default); clicking a header activates a sort and toggles
  // asc/desc on repeat clicks of the same column.
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  // Comparable value per column - strings sort case-insensitively, numbers
  // numerically; missing values sink to the bottom of an ascending sort.
  const sortValue = useCallback(
    (s: ScreenRecord, key: SortKey): string | number => {
      switch (key) {
        case "room":
          return (s.roomId ?? "").toLowerCase();
        case "role":
          return roleLabel(t, s.role).toLowerCase();
        case "checkin": {
          const knownRoom = s.roomId != null && occupancyByRoom.has(s.roomId);
          if (!knownRoom) return -1;
          return occupancyByRoom.get(s.roomId!) ? 1 : 0;
        }
        case "model":
          return (s.live?.model ?? s.model ?? "").toLowerCase();
        case "platform":
          return platformLabel(s.live?.platform).toLowerCase();
        case "status":
          return isScreenOnline(s, 60_000) ? 1 : 0;
        case "signal":
          return s.live?.wifiSignal ?? -Infinity;
        case "uptime":
          return s.live?.uptimeMs ?? -1;
        case "lastSeen": {
          const seen = Date.parse(s.lastSeenAt ?? "");
          return Number.isFinite(seen) ? seen : -Infinity;
        }
        case "app":
          return (s.live?.appVersion ?? "").toLowerCase();
        case "streaming":
          return (s.live?.streamingAppVersion ?? "").toLowerCase();
        case "firmware":
          return (s.live?.firmware ?? "").toLowerCase();
      }
    },
    [occupancyByRoom, t]
  );

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filtered, sortKey, sortDir, sortValue]);

  const headCell = (key: SortKey, label: string) => (
    <TableCell sortDirection={sortKey === key ? sortDir : false}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : "asc"}
        onClick={() => handleSort(key)}
        data-testid={`devices-sort-${key}`}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  function toggleAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((s) => s.id)));
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Open the bulk-action dialog over the current selection (frozen, so the
  // 5s refresh clearing `selected` mid-dialog doesn't drop the targets).
  function openBulk(action: BulkAction) {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkTargets(ids);
    setBulkAction(action);
  }

  // After a bulk action dispatches: surface a note (staged rollouts are managed
  // from the Rollouts page), clear the selection, and refresh.
  function onBulkDone(result: BulkActionResult, action: BulkAction) {
    const key =
      action === "reboot"
        ? "devicesView.bulkDone.reboot"
        : "devicesView.bulkDone.message";
    if (result.status === "canary" || result.status === "rolling") {
      // A staged wave - point the operator at the Rollouts page to expand it.
      const staged = result.status === "canary";
      toast.success(
        t(staged ? `${key}Canary` : key, { count: result.count }),
        staged ? { durationMs: 10000 } : undefined
      );
    } else {
      toast.success(t(key, { count: result.count }));
    }
    setSelected(new Set());
    void refresh();
  }

  /**
   * Unique room ids bound to the currently-selected devices, for a bulk
   * check-in or check-out.
   *
   * SIGNAGE SCREENS ARE EXCLUDED. A lobby wall is still filed under a room -
   * that is how it is addressed - but it has no guest, and including it here
   * meant "select all, check out" swept the room its screen happens to sit in
   * into the same action. Same defect as the per-row chip, reached through a
   * different door, and worse: it is a bulk action, so nobody is looking at the
   * one row it got wrong.
   */
  function selectedRoomIds(): string[] {
    const ids = new Set<string>();
    screens.forEach((s) => {
      if (selected.has(s.id) && s.roomId && s.role !== "signage") {
        ids.add(s.roomId);
      }
    });
    return Array.from(ids);
  }

  // Check the rooms of all selected devices in (occupied=true) or out.
  async function bulkCheckIn(occupied: boolean) {
    const roomIds = selectedRoomIds();
    if (roomIds.length === 0) return;
    setOccupancyBusy((s) => new Set([...s, ...roomIds]));
    try {
      await Promise.all(
        roomIds.map((rid) =>
          foyerApi.setRoomOccupancy(premiseId, rid, occupied)
        )
      );
      toast.success(
        occupied
          ? t("devicesView.checkedInUnits", { count: roomIds.length })
          : t("devicesView.checkedOutUnits", { count: roomIds.length })
      );
      await refresh();
    } catch (err) {
      toast.error(
        describeError(
          t,
          roomIds.length === 1
            ? occupied
              ? "checkInOneUnit"
              : "checkOutOneUnit"
            : occupied
              ? "checkInUnits"
              : "checkOutUnits",
          err,
          { count: roomIds.length }
        )
      );
      void refresh();
    } finally {
      setOccupancyBusy((s) => {
        const c = new Set(s);
        roomIds.forEach((id) => c.delete(id));
        return c;
      });
    }
  }

  // Toggle a single room's check-in from its row chip (optimistic).
  async function toggleCheckIn(roomId: string, occupied: boolean) {
    setOccupancyBusy((s) => new Set(s).add(roomId));
    try {
      await foyerApi.setRoomOccupancy(premiseId, roomId, occupied);
      setLiveRooms((prev) =>
        (prev ?? rooms).map((r) => (r.id === roomId ? { ...r, occupied } : r))
      );
      toast.success(
        driver
          ? t(
              occupied
                ? "devicesView.checkedInUnitIn"
                : "devicesView.checkedOutUnitIn",
              { room: roomId, vendor: driver.displayName }
            )
          : occupied
            ? t("devicesView.checkedInUnit", { room: roomId })
            : t("devicesView.checkedOutUnit", { room: roomId })
      );
    } catch (err) {
      toast.error(
        pmsOccupancyRefusal(t, err, roomId, occupied) ??
          describeError(t, occupied ? "checkInUnit" : "checkOutUnit", err, {
            room: roomId,
          })
      );
    } finally {
      setOccupancyBusy((s) => {
        const c = new Set(s);
        c.delete(roomId);
        return c;
      });
    }
  }

  const allSelected = selected.size > 0 && selected.size === filtered.length;
  const someSelected = selected.size > 0 && selected.size < filtered.length;

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t("devicesView.title")}
        actions={
          <>
            <PermissionGate
              resource="screens"
              level="administrate"
              place={here}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<QrCodeScannerIcon />}
                onClick={() => setProvisionOpen(true)}
                data-testid="devices-provision"
              >
                {t("devicesView.provisionDevices")}
              </Button>
            </PermissionGate>
            <PermissionGate resource="screens" level="view" place={here}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PowerIcon />}
                onClick={() => setPowerOpen(true)}
                data-testid="devices-power-schedules"
              >
                {t("devicesView.powerSchedules")}
              </Button>
            </PermissionGate>
            <Tooltip title={t("common:buttons.refresh")}>
              <IconButton
                onClick={refresh}
                aria-label={t("devicesView.refreshDevices")}
                data-testid="devices-refresh"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </>
        }
      />
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/* Toolbar */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("devicesView.searchPlaceholder")}
          size="small"
          sx={{ flex: 1, maxWidth: 360 }}
          inputProps={{ "data-testid": "devices-search" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {(allowDoorSign || allowSignage) && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={roleFilter}
            onChange={(_e, v) => v && setRoleFilter(v)}
            aria-label={t("devicesView.roleFilter.label")}
            data-testid="devices-role-filter"
          >
            <ToggleButton value="all" data-testid="devices-role-filter-all">
              {t("devicesView.roleFilter.all")}
            </ToggleButton>
            <ToggleButton value="tv" data-testid="devices-role-filter-tv">
              {t("devicesView.roleFilter.tv")}
            </ToggleButton>
            {allowDoorSign && (
              <ToggleButton
                value="door-sign"
                data-testid="devices-role-filter-door-sign"
              >
                {t("devicesView.roleFilter.doorSign")}
              </ToggleButton>
            )}
            {allowSignage && (
              <ToggleButton
                value="signage"
                data-testid="devices-role-filter-signage"
              >
                {t("common:labels.signage")}
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        )}
        {selected.size > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {t("devicesView.selectedCount", { count: selected.size })}
            </Typography>
            <PermissionGate resource="screens" level="edit" place={here}>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={() => openBulk("reboot")}
                data-testid="devices-bulk-reboot"
              >
                {t("devicesView.bulk.reboot")}
              </Button>
            </PermissionGate>
            <PermissionGate
              resource="screens/messages"
              level="edit"
              place={here}
            >
              <Button
                size="small"
                startIcon={<MessageIcon />}
                onClick={() => openBulk("message")}
                data-testid="devices-bulk-message"
              >
                {t("devicesView.bulk.message")}
              </Button>
            </PermissionGate>
            {/* Bulk check-in beside a PMS that owns check-ins would be a
                second trigger for a whole floor; the server refuses it too. */}
            {!occupancyDerived && !driver && (
              <>
                <Button
                  size="small"
                  startIcon={<MeetingRoomIcon />}
                  onClick={() => bulkCheckIn(true)}
                  data-testid="devices-bulk-checkin"
                >
                  {t("devicesView.bulk.checkIn")}
                </Button>
                <Button
                  size="small"
                  startIcon={<NoMeetingRoomIcon />}
                  onClick={() => bulkCheckIn(false)}
                  data-testid="devices-bulk-checkout"
                >
                  {t("devicesView.bulk.checkOut")}
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>

      {filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {query
            ? t("devicesView.empty.noMatch", { query })
            : roleFilter === "signage"
              ? t("devicesView.empty.signage")
              : roleFilter === "door-sign"
                ? t("devicesView.empty.doorSign")
                : roleFilter === "tv"
                  ? t("devicesView.empty.tv")
                  : t("devicesView.empty.none")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="screens-table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  />
                </TableCell>
                {headCell("room", t("devicesView.columns.unit"))}
                {(allowDoorSign || allowSignage) &&
                  headCell("role", t("devicesView.columns.role"))}
                {headCell(
                  "checkin",
                  occupancyDerived
                    ? t("devicesView.columns.occupancy")
                    : t("devicesView.columns.checkIn")
                )}
                {headCell("model", t("devicesView.columns.model"))}
                {headCell("platform", t("devicesView.columns.platform"))}
                {headCell("status", t("common:labels.status"))}
                {headCell("signal", t("devicesView.columns.signal"))}
                {headCell("uptime", t("devicesView.columns.uptime"))}
                {headCell("lastSeen", t("devicesView.columns.lastSeen"))}
                {headCell("app", t("devicesView.columns.app"))}
                {headCell("streaming", t("devicesView.columns.streaming"))}
                {headCell("firmware", t("devicesView.columns.firmware"))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((s) => {
                const online = isScreenOnline(s, 60_000);
                const knownRoom =
                  s.roomId != null && occupancyByRoom.has(s.roomId);
                const checkedIn = knownRoom && occupancyByRoom.get(s.roomId!);
                return (
                  <TableRow
                    key={s.id}
                    hover
                    selected={selected.has(s.id)}
                    sx={{ cursor: "pointer" }}
                    onClick={() => setDetail(s)}
                    data-testid={`device-row-${s.id}`}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selected.has(s.id)}
                        onChange={() => toggle(s.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body2"
                          fontWeight="fontWeightMedium"
                        >
                          {s.roomId ?? "-"}
                        </Typography>
                        {s.demo && (
                          <Tooltip title={t("devicesView.demoHint")}>
                            <Chip
                              size="small"
                              label={t("devicesView.demoChip")}
                              color="warning"
                              variant="outlined"
                              sx={{
                                fontWeight: "fontWeightSemibold",
                                letterSpacing: "0.05em",
                                typography: "micro",
                                height: 18,
                              }}
                              data-testid={`device-demo-${s.id}`}
                            />
                          </Tooltip>
                        )}
                        {s.provisioned && (
                          <Tooltip title={t("devicesView.awaitingHint")}>
                            <Chip
                              size="small"
                              label={t("devicesView.awaitingChip")}
                              color="info"
                              variant="outlined"
                              sx={{
                                fontWeight: "fontWeightSemibold",
                                letterSpacing: "0.05em",
                                typography: "micro",
                                height: 18,
                              }}
                              data-testid={`device-awaiting-${s.id}`}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    {(allowDoorSign || allowSignage) && (
                      <TableCell>
                        <RoleChip
                          role={s.role}
                          id={s.id}
                          playback={s.live?.signage}
                          online={online}
                          lastSeenAt={s.lastSeenAt}
                        />
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {s.role === "signage" ? (
                        /*
                         * A signage screen has no guest.
                         *
                         * Worse than merely meaningless: this chip is clickable
                         * and toggles the occupancy of the ROOM the screen is
                         * filed under, so checking in a lobby wall checks in a
                         * room - and on a care premise that is an admission
                         * record. A door sign keeps the column, because a
                         * corridor sign belongs to exactly one room and its
                         * occupancy is the thing it exists to show.
                         *
                         * Tooltipped rather than blank so it reads as "does not
                         * apply" instead of "we do not know", which is what the
                         * unknown-room dash below means.
                         */
                        <Tooltip title={t("devicesView.occupancy.signageNa")}>
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            data-testid={`device-checkin-na-${s.id}`}
                          >
                            -
                          </Typography>
                        </Tooltip>
                      ) : !knownRoom ? (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      ) : occupancyDerived ? (
                        // Status only. Admit/discharge under Residents is the
                        // real action - see `occupancyDerived`.
                        <Tooltip title={t("devicesView.occupancy.derivedHint")}>
                          <Chip
                            size="small"
                            color={checkedIn ? "success" : "default"}
                            variant={checkedIn ? "filled" : "outlined"}
                            label={
                              checkedIn
                                ? t("devicesView.occupancy.occupied")
                                : t("devicesView.occupancy.vacant")
                            }
                            data-testid={`device-checkin-${s.id}`}
                          />
                        </Tooltip>
                      ) : driver && !driver.canWrite ? (
                        <Tooltip
                          title={t("devicesView.occupancy.followsReadOnly", {
                            vendor: driver.displayName,
                          })}
                        >
                          <Chip
                            size="small"
                            color={checkedIn ? "success" : "default"}
                            variant={checkedIn ? "filled" : "outlined"}
                            label={
                              checkedIn
                                ? t("devicesView.occupancy.checkedIn")
                                : t("devicesView.occupancy.vacant")
                            }
                            data-testid={`device-checkin-${s.id}`}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            driver
                              ? t(
                                  checkedIn
                                    ? "devicesView.occupancy.followsCheckOut"
                                    : "devicesView.occupancy.followsCheckIn",
                                  { vendor: driver.displayName }
                                )
                              : checkedIn
                                ? t("devicesView.occupancy.clickToCheckOut")
                                : t("devicesView.occupancy.clickToCheckIn")
                          }
                        >
                          <Chip
                            size="small"
                            clickable
                            disabled={occupancyBusy.has(s.roomId!)}
                            color={checkedIn ? "success" : "default"}
                            variant={checkedIn ? "filled" : "outlined"}
                            label={
                              checkedIn
                                ? t("devicesView.occupancy.checkedIn")
                                : t("devicesView.occupancy.vacant")
                            }
                            onClick={() => toggleCheckIn(s.roomId!, !checkedIn)}
                            data-testid={`device-checkin-${s.id}`}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {s.live?.model ?? s.model}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {s.live?.platform ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={platformLabel(s.live.platform)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={online ? "success" : "default"}
                        label={
                          online
                            ? t("devicesView.status.online")
                            : t("devicesView.status.offline")
                        }
                      />
                    </TableCell>
                    <TableCell data-testid={`device-signal-${s.id}`}>
                      <SignalCell
                        dBm={s.live?.wifiSignal}
                        ethIp={s.live?.ethIp}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatUptime(t, s.live?.uptimeMs)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {relativeTime(t, s.lastSeenAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "fontFamilyMono",
                          color: "text.secondary",
                        }}
                      >
                        {s.live?.appVersion ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "fontFamilyMono",
                          color: "text.secondary",
                        }}
                      >
                        {s.live?.streamingAppVersion ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "fontFamilyMono",
                          color: "text.secondary",
                        }}
                      >
                        {s.live?.firmware ?? "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeviceDetailDialog
        open={detail !== null}
        premiseId={premiseId}
        screen={detail}
        allowDoorSign={allowDoorSign}
        allowSignage={allowSignage}
        showGuestCasting={showGuestCasting}
        onClose={() => setDetail(null)}
        onChanged={refresh}
        onOpenPreview={(s, role) => {
          openScreenPreview({
            premiseId,
            screenId: s.id,
            role,
            rooms,
            roomId: s.roomId ?? undefined,
          });
          setDetail(null);
        }}
      />

      <BulkActionDialog
        open={bulkAction !== null}
        premiseId={premiseId}
        action={bulkAction ?? "reboot"}
        screenIds={bulkTargets}
        onClose={() => setBulkAction(null)}
        onDone={onBulkDone}
      />

      <ProvisioningDialog
        open={provisionOpen}
        premiseId={premiseId}
        rooms={rooms}
        allowDoorSign={allowDoorSign}
        onClose={() => setProvisionOpen(false)}
        onChanged={refresh}
      />

      <PowerScheduleDialog
        open={powerOpen}
        premiseId={premiseId}
        onClose={() => setPowerOpen(false)}
      />
    </Stack>
  );
}
